import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

export interface DeploymentNotification {
  version: string;
}

export interface AppVersionResponse {
  success: boolean;
  data: {
    web: {
      version: string;
      forceUpdate: boolean;
    };
  };
  message: string;
}

/**
 * Deployment reload toast — ported from nest-autoshop web-shop (SSE → version API compare).
 * Pusher (or tab focus) is only a wake-up; the toast shows when GET /app/version differs from
 * the in-memory baseline for this tab.
 *
 * Version baseline and toast dedup are per-tab in memory so one tab reloading does not
 * silence deploy toasts in other tabs via shared localStorage.
 */
@Injectable({
  providedIn: 'root',
})
export class DeploymentNotificationService implements OnDestroy {
  private readonly VERSION_API_URL = `${environment.baseUrl}/${environment.apiVersion}/app/version`;
  private readonly NOTIFICATION_KEY = 'deployment-update';
  /** Legacy deploy keys — cleared once on init; no longer written. */
  private readonly LEGACY_LOCAL_STORAGE_VERSION_KEY = 'appVersion';
  private readonly LEGACY_SESSION_STORAGE_VERSION_KEY = 'appVersion';
  private readonly LEGACY_TOAST_SHOWN_KEY = 'deploymentToastShownFor';
  private readonly LEGACY_SESSION_TOAST_SHOWN_KEY = 'toastShownFor';
  private readonly isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

  /** Deployed version this tab loaded with (from API or first SSE). */
  private baselineVersion: string | null = null;
  /** Deploy version we already showed the reload toast for this tab session. */
  private toastShownForVersion: string | null = null;

  private isCheckingVersion = false;
  private windowFocusListener: (() => void) | null = null;
  private baselineInitPromise: Promise<void> | null = null;
  private baselineInitComplete = false;
  /** SSE deploy version received before baseline init finishes. */
  private pendingDeployedVersion: string | null = null;

  constructor(
    private messageService: ToastService,
    private http: HttpClient
  ) {
    if (!this.isBrowser) {
      return;
    }
    this.setupWindowFocusListener();
    this.baselineInitPromise = this.initializeVersionTracking();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private async initializeVersionTracking(): Promise<void> {
    this.clearLegacyVersionStorage();
    try {
      const versionResponse = await this.fetchCurrentVersion();
      this.baselineVersion = versionResponse.data.web.version;
    } catch (error) {
      console.warn('Failed to initialize version tracking:', error);
    } finally {
      this.baselineInitComplete = true;
      this.processPendingDeployedVersion();
    }
  }

  private processPendingDeployedVersion(): void {
    if (!this.pendingDeployedVersion) {
      return;
    }
    const pending = this.pendingDeployedVersion;
    this.pendingDeployedVersion = null;

    if (!this.baselineVersion) {
      this.maybeShowToast(pending);
      return;
    }

    if (pending !== this.baselineVersion) {
      this.maybeShowToast(pending);
    }
  }

  private setupWindowFocusListener(): void {
    this.windowFocusListener = () => {
      if (!document.hidden) {
        void this.checkForNewVersion();
      }
    };
    window.addEventListener('focus', this.windowFocusListener);
    document.addEventListener('visibilitychange', this.windowFocusListener);
  }

  async checkForNewVersion(): Promise<void> {
    if (!this.isBrowser || this.isCheckingVersion) {
      return;
    }
    this.isCheckingVersion = true;
    try {
      if (this.baselineInitPromise) {
        await this.baselineInitPromise;
      }
      const versionResponse = await this.fetchCurrentVersionWithRetry();
      const deployedVersion = versionResponse.data.web.version;
      if (!this.baselineVersion) {
        this.baselineVersion = deployedVersion;
        return;
      }
      if (deployedVersion !== this.baselineVersion) {
        this.maybeShowToast(deployedVersion);
      }
    } catch (error) {
      console.warn('Failed to check for new version after retries:', error);
    } finally {
      this.isCheckingVersion = false;
    }
  }

  private fetchCurrentVersion(): Promise<AppVersionResponse> {
    return firstValueFrom(this.http.get<AppVersionResponse>(this.VERSION_API_URL));
  }

  private async fetchCurrentVersionWithRetry(maxRetries = 3): Promise<AppVersionResponse> {
    let retryCount = 0;
    let lastError: Error = new Error('Unknown error');
    while (retryCount < maxRetries) {
      try {
        return await this.fetchCurrentVersion();
      } catch (error) {
        lastError = error as Error;
        retryCount += 1;
        if (retryCount < maxRetries) {
          const delayMs = Math.pow(2, retryCount - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
    throw lastError;
  }

  /** Pusher/SSE wake-up — version API is the source of truth for showing the toast. */
  async handleDeploymentNotification(_notification: DeploymentNotification): Promise<void> {
    await this.checkForNewVersion();
  }

  /**
   * Show reload toast when SSE (or another source) already provides the deployed version.
   * Used by the SSE pilot so we don't depend on GET /app/version matching the test payload.
   */
  notifyDeployedVersion(deployedVersion: string): void {
    if (!this.isBrowser || !deployedVersion) {
      return;
    }
    if (!this.baselineInitComplete) {
      this.pendingDeployedVersion = deployedVersion;
      return;
    }
    if (!this.baselineVersion) {
      this.maybeShowToast(deployedVersion);
      return;
    }
    if (deployedVersion === this.baselineVersion) {
      console.info('[DeploySSE] version unchanged vs baseline', {
        deployedVersion,
        baselineVersion: this.baselineVersion,
      });
      return;
    }
    this.maybeShowToast(deployedVersion);
  }

  private maybeShowToast(deployedVersion: string): void {
    if (this.toastShownForVersion === deployedVersion) {
      return;
    }
    this.toastShownForVersion = deployedVersion;
    this.showDeploymentToast({ version: deployedVersion });
  }

  private showDeploymentToast(notification: DeploymentNotification): void {
    this.messageService.clear(this.NOTIFICATION_KEY);
    this.messageService.add({
      key: this.NOTIFICATION_KEY,
      severity: 'info',
      summary: 'New Release Available',
      detail: 'Refresh to access the latest features and fixes.',
      sticky: true,
      closable: false,
      styleClass: 'deployment-update-toast',
      data: {
        isDeploymentUpdate: true,
        version: notification.version,
        linkLabel: 'Reload Now',
      },
    });
  }

  reloadApplication(): void {
    if (!this.isBrowser) {
      return;
    }
    // TODO: Material snackbar doesn't support named keys for clearing
    // this.messageService.clear(this.NOTIFICATION_KEY);
    this.baselineVersion = null;
    this.toastShownForVersion = null;
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  /** Drop stale cross-tab deploy keys; LPR notification_history is untouched. */
  private clearLegacyVersionStorage(): void {
    if (!this.isBrowser) {
      return;
    }
    localStorage.removeItem(this.LEGACY_LOCAL_STORAGE_VERSION_KEY);
    localStorage.removeItem(this.LEGACY_TOAST_SHOWN_KEY);
    sessionStorage.removeItem(this.LEGACY_SESSION_STORAGE_VERSION_KEY);
    sessionStorage.removeItem(this.LEGACY_SESSION_TOAST_SHOWN_KEY);
  }

  private cleanup(): void {
    if (!this.isBrowser) {
      return;
    }
    if (this.windowFocusListener) {
      window.removeEventListener('focus', this.windowFocusListener);
      document.removeEventListener('visibilitychange', this.windowFocusListener);
      this.windowFocusListener = null;
    }
  }
}
